import React, { useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputBase,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FilterListOffRoundedIcon from "@mui/icons-material/FilterListOffRounded";

/** Shell visual compartilhado dos popups de filtro do Dashboard. */
export function DashboardFilterModalFrame({
  open,
  onClose,
  icon,
  title,
  subtitle,
  loading = false,
  children,
  footer,
  maxWidth = "sm",
}) {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          maxHeight: { xs: "92vh", sm: "88vh" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          px: 2.5,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          pr: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
            "& .MuiSvgIcon-root": { fontSize: 22 },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, pt: 0.15 }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              display: "block",
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.4, lineHeight: 1.35, fontSize: "0.8125rem" }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Fechar"
          sx={{ color: "text.secondary", mt: -0.25 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{
          px: 2.5,
          py: 2,
          overflowY: "auto",
          bgcolor: alpha(theme.palette.text.primary, 0.015),
        }}
      >
        {loading ? (
          <Box sx={{ py: 7, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>{children}</Box>
        )}
      </DialogContent>

      {footer}
    </Dialog>
  );
}

export function DashboardFilterSection({ title, hint, selectedCount = 0, children }) {
  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: hint ? 0.5 : 1.1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
          {title}
        </Typography>
        {selectedCount > 0 ? (
          <Chip
            size="small"
            label={selectedCount}
            color="primary"
            sx={{
              height: 22,
              minWidth: 22,
              fontWeight: 700,
              fontSize: "0.7rem",
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        ) : null}
      </Box>
      {hint ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.1, lineHeight: 1.35 }}
        >
          {hint}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

export function DashboardMultiPickList({
  options,
  value,
  onChange,
  getLabel,
  getValue,
  searchPlaceholder,
  emptyLabel,
  valueAsString = false,
  maxHeight = 168,
}) {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const selected = useMemo(
    () => new Set(value.map((v) => (valueAsString ? String(v) : Number(v)))),
    [value, valueAsString]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => getLabel(opt).toLowerCase().includes(q));
  }, [options, search, getLabel]);

  const toggle = (rawId) => {
    const id = valueAsString ? String(rawId) : Number(rawId);
    if (valueAsString) {
      if (!id) return;
      if (selected.has(id)) onChange(value.filter((x) => String(x) !== id));
      else onChange([...value, id]);
      return;
    }
    if (!Number.isFinite(id) || id < 0) return;
    if (selected.has(id)) onChange(value.filter((x) => Number(x) !== id));
    else onChange([...value, id]);
  };

  return (
    <Box>
      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          startAdornment={
            <InputAdornment position="start">
              <SearchRoundedIcon fontSize="small" color="action" />
            </InputAdornment>
          }
          endAdornment={
            selected.size > 0 ? (
              <InputAdornment position="end">
                <Tooltip title="Limpar seleção">
                  <IconButton
                    size="small"
                    onClick={() => onChange([])}
                    aria-label="Limpar seleção"
                    edge="end"
                  >
                    <FilterListOffRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null
          }
          sx={{
            px: 1.25,
            py: 0.7,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            fontSize: "0.8125rem",
            bgcolor: alpha(theme.palette.text.primary, 0.02),
            "&:hover": { borderColor: "action.disabled" },
            "&.Mui-focused, &:focus-within": {
              borderColor: "primary.main",
              bgcolor: "background.paper",
            },
          }}
        />
      </FormControl>
      <Box
        sx={{
          maxHeight,
          overflowY: "auto",
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        {filtered.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: "center", fontSize: "0.8125rem" }}
          >
            {emptyLabel}
          </Typography>
        ) : (
          filtered.map((opt) => {
            const id = getValue(opt);
            const key = valueAsString ? String(id) : Number(id);
            const checked = selected.has(key);
            return (
              <ListItemButton
                key={String(id)}
                dense
                onClick={() => toggle(id)}
                sx={{
                  py: 0.4,
                  px: 1,
                  bgcolor: checked ? alpha(theme.palette.primary.main, 0.06) : "transparent",
                  "&:hover": {
                    bgcolor: checked
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.action.hover, 0.6),
                  },
                }}
              >
                <Checkbox
                  edge="start"
                  size="small"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                  sx={{ mr: 0.25 }}
                />
                <ListItemText
                  primary={getLabel(opt)}
                  primaryTypographyProps={{
                    variant: "body2",
                    noWrap: true,
                    fontWeight: checked ? 600 : 400,
                    fontSize: "0.8125rem",
                  }}
                />
              </ListItemButton>
            );
          })
        )}
      </Box>
    </Box>
  );
}

export function DashboardFilterModalActions({
  onClear,
  onClose,
  onApply,
  hasActive,
  clearLabel = "Limpar",
  cancelLabel = "Cancelar",
  applyLabel = "Aplicar",
}) {
  return (
    <DialogActions
      sx={{
        px: 2.5,
        py: 1.75,
        gap: 1,
        borderTop: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Button
        onClick={onClear}
        color="inherit"
        disabled={!hasActive}
        startIcon={<FilterListOffRoundedIcon sx={{ fontSize: 18 }} />}
        sx={{ textTransform: "none", color: "text.secondary" }}
      >
        {clearLabel}
      </Button>
      <Box sx={{ flex: 1 }} />
      <Button onClick={onClose} sx={{ textTransform: "none", color: "text.secondary" }}>
        {cancelLabel}
      </Button>
      <Button
        variant="contained"
        onClick={onApply}
        startIcon={<TuneRoundedIcon />}
        sx={{ textTransform: "none", fontWeight: 700, px: 2, borderRadius: 2, boxShadow: "none" }}
      >
        {applyLabel}
      </Button>
    </DialogActions>
  );
}
